import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useQuery, useMutation } from 'react-apollo';
import { connect } from 'react-redux';
import { get, flatten, cloneDeep, clone } from 'lodash';
import { withTranslation } from 'react-i18next';
import QcardPanel from '_Components/QcardPanel/QcardPanel';
import { HintsContent } from '_Components/QcardPanel/QcardHeader/QcardHints/Hint';
import Icon from '_Components/Icons/Icon';
import VarianceAnalysisChart from '_Charts/AnalysesCharts/VarianceAnalysisChart/VarianceAnalysisChart';
import GET_THEMATIC_SELECTION_LIST from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/getThematicSelectionList';
import { setThematicSelectionList } from '_Resources/Cards/Actions/setThematicSelectionList';
import GET_THEMATICS_OVERVIEW_DATA from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/getThematicsOverviewData';
import {
  getDefaultSettings,
  getSelectedThematicsForSettings,
} from '_Cards/ThematicsCard/ThematicsOverviewCards/CategoryBreakdownCard/utils';
import { selectThematicSelection } from '_Resources/Cards/Actions/selectThematicSelection';
import SAVE_THEMATICS_OVERVIEW_SETTINGS_MUTATION from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/saveThematicsOverviewSettings';
import GET_DATA_ANALYSIS_QUERY from './graphql/getVarianceAnalysis';
import DropdownsContainer from './_Containers/DropdownsContainer/DropdownsContainer';
import { MAIN_INDICATOR_CONFIG, sortData } from './_Containers/DropdownsContainer/utils';

const GraphContainer = styled.div`
  height: 35em;
`;
const spinning = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Loader = styled(Icon)`
  animation: ${spinning} 1s linear infinite;
`;

type ThematicSelectionType = {
  createdBy: {
    firstName: string;
    lastName: string;
    email: string;
  };
  id: number;
  label: string;
  name: string;
  projectId: number;
  thematics: Array<{
    thematic: string;
    subThematic: string | null;
    subSubThematic: string | null;
  }>;
};

type VarianceAnalysisCardProps = {
  filterKeys: any;
  isEindexUnitFahrenheit: boolean;
  projectMonths: any[];
  t: Function;
  projectId: number;
  range: { startDate: string; endDate: string };
  selectionList: Array<ThematicSelectionType>;
  dispatchSetThematicSelectionList: Function;
  selectedThematicSelectionOption: ThematicSelectionType;
  dispatchSelectThematicSelection: Function;
};

const VarianceAnalysisCard = ({
  filterKeys,
  isEindexUnitFahrenheit,
  projectMonths,
  t,
  projectId,
  range,
  selectionList,
  dispatchSetThematicSelectionList,
  selectedThematicSelectionOption,
  dispatchSelectThematicSelection,
}: VarianceAnalysisCardProps) => {
  const [mainIndicator, setMainIndicator] = useState<string>('eindex');
  const [explainIndicator, setExplainIndicator] = useState<number>(1);
  const [orderBy, setOrderBy] = useState<string>('impactWeight_asc');
  const [details, setDetails] = useState<boolean>(false);
  const [analysis, setAnalysis] = useState<any>();
  const [thematicsToIterate, setThematicsToIterate] = useState<Array<any>>([]);
  const [thematicsTotalEffectValues, setThematicsTotalEffectValues] = useState<any>(
    thematicsToIterate &&
      thematicsToIterate.length > 0 &&
      sortData(orderBy, details, thematicsToIterate, mainIndicator, isEindexUnitFahrenheit, t) &&
      sortData(orderBy, details, thematicsToIterate, mainIndicator, isEindexUnitFahrenheit, t).totalEffect,
  );
  const [labels, setLabels] = useState<Array<string> | any[]>(
    thematicsToIterate &&
      thematicsToIterate.length > 0 &&
      sortData(orderBy, details, thematicsToIterate, mainIndicator, isEindexUnitFahrenheit, t).labels,
  );
  const [referencePeriod, setReferencePeriod] = useState<{
    startDate: string;
    endDate: string;
  } | null>(projectMonths?.[projectMonths?.length - 1]?.period || null);
  const [queryFilters, setQueryFilters] = useState({});
  const { data: thematicsOverviewData } = useQuery(GET_THEMATICS_OVERVIEW_DATA, {
    variables: {
      ...queryFilters,
      projectId,
      range,
      thematicSelectionId: selectedThematicSelectionOption?.id || null,
    },
    fetchPolicy: 'cache-and-network',
  });
  const [saveThematicsOverviewSettings] = useMutation(SAVE_THEMATICS_OVERVIEW_SETTINGS_MUTATION);
  const [settings, setSettings] = useState(thematicsOverviewData?.getThematicsOverviewData?.settings);

  let formattedData: any[] = [];

  const formatDeep = (arrayToIterate: any[], level: string | undefined, objToFill: {}) => {
    let newObjToFill = {};
    arrayToIterate?.map((item: { [x: string]: any; thematicsOverviewDetails?: any }) => {
      const childrenKey = Object.keys(item).find(i => Array.isArray(item[i]));
      newObjToFill = {
        ...objToFill,
        [level]: item.thematicsOverviewDetails.name,
      };
      formattedData = [...formattedData, newObjToFill];
      if (Array.isArray(item[childrenKey])) {
        formatDeep(item[childrenKey], childrenKey?.toLowerCase()?.substring(0, childrenKey.length - 1), newObjToFill);
      }
    });
    return formattedData;
  };

  const categoriesHierarchies = formatDeep(
    thematicsOverviewData?.getThematicsOverviewData?.thematics,
    'thematic',
    {},
  ).map(item => {
    if (!item.subthematic) {
      return { ...item, subthematic: null, subsubthematic: null };
    }
    if (!item.subsubthematic) {
      return { ...item, subsubthematic: null };
    }
    return item;
  });

  const defaultSettings = getDefaultSettings(categoriesHierarchies);

  const allThematics = {
    createdBy: {
      firstName: 'system',
      lastName: 'system',
      email: 'support@qemotion.com',
    },
    id: null,
    label: t('button:All thematics'),
    name: t('button:All thematics'),
    projectId,
    thematics: [],
  };

  useEffect(() => {
    if (filterKeys) {
      const filters = cloneDeep(filterKeys);
      filters.keywords =
        flatten(
          filterKeys.texts
            ?.filter((text: { name: string }) => text.name === 'keywords')
            .map((text: any[]) => text.values),
        ) || [];
      delete filters.texts;
      setQueryFilters(filters);
    }
  }, [filterKeys]);

  useEffect(() => {
    if (thematicsOverviewData) {
      setSettings(
        JSON.parse(thematicsOverviewData.getThematicsOverviewData.settings)
          ? {
              ...defaultSettings,
              ...JSON.parse(thematicsOverviewData.getThematicsOverviewData.settings),
              selectedThematics:
                JSON.parse(thematicsOverviewData.getThematicsOverviewData.settings).selectedThematics || formattedData,
            }
          : defaultSettings,
      );
    }
  }, [thematicsOverviewData]);

  useEffect(() => {
    setReferencePeriod(projectMonths?.[projectMonths?.length - 1]?.period);
  }, [projectMonths]);

  const { data: dataThematicSelectionList } = useQuery(GET_THEMATIC_SELECTION_LIST, {
    fetchPolicy: 'cache-and-network',
    variables: { projectId },
    skip: !projectId,
  });

  useEffect(() => {
    if (dataThematicSelectionList) {
      const optionsList = dataThematicSelectionList.getThematicSelectionList.map((selection: { name: any }) => ({
        ...selection,
        label: selection.name,
      }));
      dispatchSetThematicSelectionList(optionsList);
    }
  }, [dataThematicSelectionList]);

  const getThematicsToIterate = () => {
    switch (explainIndicator) {
      case 1:
      default:
        setThematicsToIterate(analysis?.thematics);
        break;
      case 2:
        let subThematics: Array<any> = [];
        analysis?.thematics?.map((thematic: { subThematics: string | any[] }) => {
          if (thematic.subThematics && thematic.subThematics.length > 0) {
            subThematics = [...subThematics, ...thematic.subThematics];
          } else {
            subThematics = [...subThematics, thematic];
          }
        });
        setThematicsToIterate(subThematics);
        break;
      case 3:
        let subThematicsArray: Array<any> = [];
        let subSubThematics: Array<any> = [];
        analysis?.thematics?.map((thematic: { subThematics: any }) => {
          subThematicsArray = [...subThematicsArray, ...thematic.subThematics];
        });
        subThematicsArray?.map(subThematic => {
          if (subThematic.subSubThematics && subThematic.subSubThematics.length > 0) {
            subSubThematics = [...subSubThematics, ...subThematic.subSubThematics];
          } else {
            subSubThematics = [...subSubThematics, subThematic];
          }
        });
        setThematicsToIterate(subSubThematics);
        break;
    }
  };

  useEffect(() => {
    getThematicsToIterate();
  }, [analysis, explainIndicator]);

  const { data: varianceAnalysisData, loading } = useQuery(GET_DATA_ANALYSIS_QUERY, {
    variables: {
      projectId: filterKeys?.projectId,
      currentPeriod: filterKeys?.range,
      referencePeriod,
      indicator: mainIndicator,
      level: explainIndicator,
      criteriaKeys: filterKeys?.criteriaKeys,
      thematics: filterKeys?.thematics,
      keywords: filterKeys?.keywords,
      thematicSelectionId: selectedThematicSelectionOption?.id || null,
    },
    skip: !referencePeriod,
  });

  useEffect(() => {
    setAnalysis(varianceAnalysisData?.getVarianceAnalysis);
  }, [varianceAnalysisData]);

  const referenceCommentsWithThematics =
    analysis?.globalReferenceVarianceAnalysis?.indicatorTotal /
    analysis?.globalReferenceVarianceAnalysis?.verbatimWithThematicCount;
  const globalReference = [
    analysis?.globalReferenceVarianceAnalysis?.totalEffect,
    referenceCommentsWithThematics - analysis?.globalReferenceVarianceAnalysis?.totalEffect,
    referenceCommentsWithThematics,
  ];

  const currentCommentsWithThematics =
    analysis?.globalCurrentVarianceAnalysis?.indicatorTotal /
    analysis?.globalCurrentVarianceAnalysis?.verbatimWithThematicCount;
  const globalCurrent = [
    currentCommentsWithThematics,
    analysis?.globalCurrentVarianceAnalysis?.totalEffect - currentCommentsWithThematics,
    analysis?.globalCurrentVarianceAnalysis?.totalEffect,
  ];

  useEffect(() => {
    setThematicsTotalEffectValues(
      sortData(orderBy, details, thematicsToIterate, mainIndicator, isEindexUnitFahrenheit, t) &&
        sortData(orderBy, details, thematicsToIterate, mainIndicator, isEindexUnitFahrenheit, t).totalEffect,
    );
    setLabels(sortData(orderBy, details, thematicsToIterate, mainIndicator, isEindexUnitFahrenheit, t).labels);
  }, [orderBy, details, thematicsToIterate]);

  const effectData = globalReference &&
    thematicsTotalEffectValues &&
    globalCurrent && [...globalReference, ...thematicsTotalEffectValues, ...globalCurrent];

  const negativeTotalEffect =
    effectData &&
    effectData?.map((d: number, ind: number) =>
      d > 0 || ind === 0 || ind === 2 || ind === effectData.length - 1 || ind === effectData.length - 3 ? '-' : d,
    );
  const positiveTotalEffect =
    effectData &&
    effectData?.map((d: number, ind: number) =>
      d < 0 || ind === 0 || ind === 2 || ind === effectData.length - 1 || ind === effectData.length - 3 ? '-' : d,
    );

  const totalEffect =
    effectData &&
    effectData?.map((d: any, ind: number) =>
      ind === 0 || ind === 2 || ind === effectData.length - 1 || ind === effectData.length - 3 ? d : '-',
    );

  let thematicsGraphData = [globalReference[globalReference.length - 1]];
  thematicsTotalEffectValues &&
    thematicsTotalEffectValues?.map((d: number, ind: number) => {
      thematicsGraphData = [
        ...thematicsGraphData,
        thematicsGraphData[ind]
          ? d > 0
            ? thematicsTotalEffectValues[ind - 1] > 0
              ? thematicsGraphData[ind] + thematicsTotalEffectValues[ind - 1]
              : thematicsGraphData[ind]
            : !thematicsTotalEffectValues[ind - 1] || thematicsTotalEffectValues[ind - 1] < 0
            ? thematicsGraphData[ind] + d
            : thematicsGraphData[ind] + d + thematicsTotalEffectValues[ind - 1]
          : d,
      ];
    });
  thematicsGraphData.shift();

  const graphData = [
    ...globalReference.map((d, ind) => {
      return totalEffect && totalEffect?.includes(d)
        ? 0
        : d < 0
        ? globalReference[globalReference.length - 1]
        : globalReference[globalReference.length - 1] - d;
    }),
    ...thematicsGraphData,
    ...globalCurrent.map((d, ind) => {
      return totalEffect && totalEffect?.includes(d)
        ? 0
        : d < 0
        ? globalCurrent[globalCurrent.length - 1]
        : globalCurrent[globalCurrent.length - 1] - d;
    }),
  ];

  const onThematicSelectionChange = (selectThematicSelectionOption: {
    thematics: { thematic: string; subThematic: string | undefined; subSubThematic: string | undefined }[];
    id: null;
  }) => {
    dispatchSelectThematicSelection(selectThematicSelectionOption);
    const cloneSettings = clone(settings);
    cloneSettings.selectedThematics = getSelectedThematicsForSettings(selectThematicSelectionOption.thematics);
    if (selectThematicSelectionOption.id !== null) {
      cloneSettings.categoryBreakdown.onlySelectedCategories = true;
    }
    if (selectThematicSelectionOption.id === null) {
      cloneSettings.selectedThematics = [];
      cloneSettings.categoryBreakdown.onlySelectedCategories = false;
    }
    saveThematicsOverviewSettings({
      variables: {
        projectId,
        settings: JSON.stringify(cloneSettings),
        thematicSelectionId: selectThematicSelectionOption?.id || null,
      },
      refetchQueries: [
        {
          query: GET_DATA_ANALYSIS_QUERY,
          variables: {
            projectId: filterKeys?.projectId,
            currentPeriod: filterKeys?.range,
            referencePeriod,
            indicator: mainIndicator,
            level: explainIndicator,
            criteriaKeys: filterKeys?.criteriaKeys,
            thematics: filterKeys?.thematics,
            keywords: filterKeys?.keywords,
            thematicSelectionId: null,
          },
        },
      ],
    });
  };

  useEffect(() => {
    const foundStoredThematicSelectionId = selectionList
      .map((option: { id: any }) => option.id)
      .find((id: number) => id === selectedThematicSelectionOption?.id);

    if (
      typeof projectId === 'number' &&
      typeof settings?.categoryBreakdown === 'object' &&
      ((!foundStoredThematicSelectionId && selectionList.length > 0) ||
        selectionList.length === 0 ||
        selectionList === undefined) &&
      selectedThematicSelectionOption?.id !== null
    ) {
      dispatchSelectThematicSelection(allThematics);
      const cloneSettings = cloneDeep(settings);
      cloneSettings.selectedThematics = [];
      cloneSettings.categoryBreakdown.onlySelectedCategories = false;
      saveThematicsOverviewSettings({
        variables: {
          projectId,
          settings: JSON.stringify(cloneSettings),
          thematicSelectionId: null,
        },
        refetchQueries: [
          {
            query: GET_DATA_ANALYSIS_QUERY,
            variables: {
              projectId: filterKeys?.projectId,
              currentPeriod: filterKeys?.range,
              referencePeriod,
              indicator: mainIndicator,
              level: explainIndicator,
              criteriaKeys: filterKeys?.criteriaKeys,
              thematics: filterKeys?.thematics,
              keywords: filterKeys?.keywords,
              thematicSelectionId: null,
            },
          },
        ],
      }).then(() => {
        if (varianceAnalysisData) {
          setAnalysis(varianceAnalysisData?.getVarianceAnalysis);
        }
      });
    }
  }, [settings, selectionList]);

  const currentThematicSelection =
    selectionList.find(option => option.id === selectedThematicSelectionOption?.id) || selectionList[0] || null;

  return (
    <QcardPanel
      title={`
      ${t(`Variance analysis`, {
        indicator: MAIN_INDICATOR_CONFIG.find(d => d.value === mainIndicator).label,
      })}
      (${details ? t('VolumeTonalityMixEffects') : t('Global')})`}
      referencePeriod={referencePeriod}
      referenceMentions={analysis?.globalReferenceVarianceAnalysis?.verbatimAmount}
      hints={[
        HintsContent.HINT_VARIANCE_ANALYSIS_INTRODUCTION,
        HintsContent.HINT_VARIANCE_ANALYSIS_TONALITY_VOLUME_EFFECTS,
        HintsContent.HINT_VARIANCE_ANALYSIS_MIX_EFFECT,
      ]}
    >
      <DropdownsContainer
        setExplainIndicator={setExplainIndicator}
        explainIndicator={explainIndicator}
        setMainIndicator={setMainIndicator}
        mainIndicator={mainIndicator}
        setReferencePeriod={setReferencePeriod}
        referencePeriod={referencePeriod}
        setDetails={setDetails}
        details={details}
        setOrderBy={setOrderBy}
        orderBy={orderBy}
        onThematicSelectionChange={onThematicSelectionChange}
        currentThematicSelection={currentThematicSelection}
        thematicSelectionsOptions={[allThematics, ...selectionList]}
      />
      <div
        style={{
          width: '100%',
          display: 'inline-flex',
          justifyContent: 'center',
        }}
      >
        {loading && (
          <b>
            <Loader icon="INTERFACE_LOADING" /> {t('Loading')}
          </b>
        )}
      </div>
      <GraphContainer>
        <VarianceAnalysisChart
          data={{
            labels: thematicsToIterate && [
              t('All comments (Reference period)'),
              t('Comments without categories'),
              t('Comments with categories (Reference period)'),
              ...(labels || [' ']),
              t('Comments with categories (Current period)'),
              t('Comments without categories'),
              t('All comments (Current period)'),
            ],
            values: {
              negativeTotalEffect: negativeTotalEffect && negativeTotalEffect?.map((d: number) => Math.abs(d)),
              positiveTotalEffect: positiveTotalEffect && positiveTotalEffect?.map((d: any) => d),
              totalEffect: totalEffect && totalEffect?.map((d: number) => Math.round(d * 1000) / 1000),
              graphData,
            },
          }}
          details={details}
          mainIndicator={mainIndicator}
          isEindexUnitFahrenheit={isEindexUnitFahrenheit}
        />
      </GraphContainer>
    </QcardPanel>
  );
};

const mapStateToProps = (state: any) => ({
  filterKeys: get(state, ['filters', 'filterKeys']),
  isEindexUnitFahrenheit: false,
  projectMonths: get(state, ['periods', 'projectPeriods', 'byMonth']),
  projectId: get(state, ['projects', 'currentProject', 'id']),
  range: get(state, ['periods', 'currentPeriod']),
  selectionList: get(state, ['thematicSelections', 'list']),
  selectedThematicSelectionOption: get(state, ['thematicSelections', 'selectedThematicSelectionOption']),
});

const mapDispatchToProps = (dispatch: any) => ({
  dispatchSetThematicSelectionList: (thematicSelectionList: Object[]) => {
    dispatch(setThematicSelectionList(thematicSelectionList));
  },
  dispatchSelectThematicSelection: (thematicSelection: any) => {
    dispatch(selectThematicSelection(thematicSelection));
  },
});

export default withTranslation('card')(connect(mapStateToProps, mapDispatchToProps)(VarianceAnalysisCard));
